import type { AnnotationTextSize, AnnotationTool } from '@annoteer/annotation-engine';
import type { ReactNode } from 'react';

const COLORS = [
  '#dc2626', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#000000', // black
];

const STROKE_WIDTHS = [1, 2, 4, 6, 10];

/**
 * Text and sticky-note size. Always visible, in the colour row rather than a
 * hover popover like stroke width: a phone has no hover, and the size is
 * usually wanted AFTER a note is placed, when the tool has returned to select.
 */
const TEXT_SIZES: Array<{ id: AnnotationTextSize; label: string; glyphPx: number }> = [
  { id: 'small', label: 'Small', glyphPx: 9 },
  { id: 'medium', label: 'Medium', glyphPx: 12 },
  { id: 'large', label: 'Large', glyphPx: 15 },
];

const STROKE_TOOLS: AnnotationTool[] = [
  'freehand',
  'rectangle',
  'ellipse',
  'triangle',
  'line',
  'arrow',
];

export interface AnnotationToolbarProps {
  activeTool: AnnotationTool;
  activeColor: string;
  activeStrokeWidth: number;
  onToolChange: (tool: AnnotationTool) => void;
  onColorChange: (color: string) => void;
  onStrokeWidthChange: (width: number) => void;
  /** Size for new text and sticky notes, and for any selected ones. */
  activeTextSize: AnnotationTextSize;
  onTextSizeChange: (size: AnnotationTextSize) => void;
}

/* Inline SVG icons (Lucide-style, 16x16) */
function IconSelect() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 2l4.5 12 1.8-5.2L14.5 7z" />
    </svg>
  );
}

function IconDraw() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12.5 2.5a1.77 1.77 0 012.5 2.5L5.5 14.5 1 15.5l1-4.5z" />
    </svg>
  );
}

function IconText() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3h10M8 3v10" />
    </svg>
  );
}

function IconHighlight() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.5 2.5l3 3-7 7H3.5v-3z" />
      <path d="M2 14h12" />
    </svg>
  );
}

function IconRect() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="3" width="12" height="10" rx="1" />
    </svg>
  );
}

function IconEllipse() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="8" cy="8" rx="6" ry="4.5" />
    </svg>
  );
}

function IconTriangle() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 2L14 14H2z" />
    </svg>
  );
}

function IconLine() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="13" x2="13" y2="3" />
    </svg>
  );
}

function IconArrow() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="13" x2="13" y2="3" />
      <polyline points="7 3 13 3 13 9" />
    </svg>
  );
}

function IconNote() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 2h12v10l-4 4H2z" />
      <path d="M10 12v4" />
      <path d="M10 12h4" />
    </svg>
  );
}

const TOOLS: Array<{ id: AnnotationTool; label: string; icon: () => ReactNode }> = [
  { id: 'select', label: 'Select', icon: IconSelect },
  { id: 'freehand', label: 'Draw', icon: IconDraw },
  { id: 'text', label: 'Text', icon: IconText },
  { id: 'highlight', label: 'Highlight', icon: IconHighlight },
  { id: 'rectangle', label: 'Rect', icon: IconRect },
  { id: 'ellipse', label: 'Ellipse', icon: IconEllipse },
  { id: 'triangle', label: 'Triangle', icon: IconTriangle },
  { id: 'line', label: 'Line', icon: IconLine },
  { id: 'arrow', label: 'Arrow', icon: IconArrow },
  { id: 'sticky-note', label: 'Note', icon: IconNote },
];

export function AnnotationToolbar({
  activeTool,
  activeColor,
  activeStrokeWidth,
  onToolChange,
  onColorChange,
  onStrokeWidthChange,
  activeTextSize,
  onTextSizeChange,
}: AnnotationToolbarProps) {
  const showStrokeWidths = STROKE_TOOLS.includes(activeTool);

  return (
    <div className="annotation-toolbar">
      <div className="annotation-toolbar__tools">
        {TOOLS.map((tool) => {
          const isActive = activeTool === tool.id;
          const isStrokeTool = STROKE_TOOLS.includes(tool.id);
          return (
            <button
              key={tool.id}
              className={`annotation-toolbar__btn ${
                isActive ? 'annotation-toolbar__btn--active' : ''
              } ${isStrokeTool ? 'annotation-toolbar__btn--stroke-tool' : ''}`}
              onClick={() => onToolChange(tool.id)}
              title={tool.label + (isActive && isStrokeTool ? ' — hover for stroke width' : '')}
              aria-label={tool.label}
              aria-pressed={isActive}
            >
              <span className="annotation-toolbar__btn-icon">{tool.icon()}</span>
              {isActive && isStrokeTool && (
                <span className="annotation-toolbar__stroke-indicator" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {showStrokeWidths && (
        <div className="annotation-toolbar__weights-popover" role="group" aria-label="Stroke width">
          {STROKE_WIDTHS.map((w) => (
            <button
              key={w}
              className={`annotation-toolbar__weight-btn ${
                activeStrokeWidth === w ? 'annotation-toolbar__weight-btn--active' : ''
              }`}
              onClick={() => onStrokeWidthChange(w)}
              title={`${w}px`}
              aria-label={`Stroke width ${w}px`}
              aria-pressed={activeStrokeWidth === w}
            >
              <span
                className="annotation-toolbar__weight-line"
                style={{ height: w, backgroundColor: activeColor }}
              />
            </button>
          ))}
        </div>
      )}

      <div className="annotation-toolbar__divider" />

      <div className="annotation-toolbar__colors">
        {COLORS.map((color) => (
          <button
            key={color}
            className={`annotation-toolbar__color-swatch ${
              activeColor === color ? 'annotation-toolbar__color-swatch--active' : ''
            }`}
            style={{ backgroundColor: color }}
            onClick={() => onColorChange(color)}
            title={color}
            aria-pressed={activeColor === color}
          />
        ))}
        <span className="annotation-toolbar__sizes-sep" aria-hidden="true" />
        <span className="annotation-toolbar__sizes" role="group" aria-label="Text size">
          {TEXT_SIZES.map((s) => (
            <button
              key={s.id}
              className={`annotation-toolbar__size-btn ${
                activeTextSize === s.id ? 'annotation-toolbar__size-btn--active' : ''
              }`}
              onClick={() => onTextSizeChange(s.id)}
              title={`Text size: ${s.label}`}
              aria-label={`Text size ${s.label.toLowerCase()}`}
              aria-pressed={activeTextSize === s.id}
            >
              <span style={{ fontSize: s.glyphPx }}>A</span>
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}
